/**
 * Dynamic text fitting — scale an element's font-size so its text fills the
 * available box without overflowing. Clue lengths, category names, and dollar
 * values vary a lot, so static `clamp()` sizes either clip the long ones or
 * leave the short ones tiny. This binary-searches the largest font that fits.
 *
 * Markup contract: a fittable element carries `data-fit`:
 *   "fill" — wrapping block text (clues, category names): grows until it fills
 *            its parent's content box by height with no word overflowing width.
 *   "val"  — board dollar values: sized uniformly across the board (all cells
 *            share the size that fits the widest value) so a column looks even.
 *
 * Call applyFits(root) after mounting a view (in rAF) and on window resize.
 */

function contentBox(parent: HTMLElement): { w: number; h: number } {
  const s = getComputedStyle(parent);
  const padX = parseFloat(s.paddingLeft) + parseFloat(s.paddingRight);
  const padY = parseFloat(s.paddingTop) + parseFloat(s.paddingBottom);
  return { w: parent.clientWidth - padX, h: parent.clientHeight - padY };
}

/** Largest font-size (px) at which `el` fits its parent's content box. */
function fitFillSize(el: HTMLElement, min: number, max: number): number {
  const parent = el.parentElement;
  if (!parent) return min;
  const { w, h } = contentBox(parent);
  if (w <= 0 || h <= 0) return min;
  let lo = min;
  let hi = max;
  for (let i = 0; i < 14; i++) {
    const mid = (lo + hi) / 2;
    el.style.fontSize = `${mid}px`;
    // Fits if it doesn't overflow height and no single word overflows width.
    const fits = el.scrollHeight <= h + 0.5 && el.scrollWidth <= el.clientWidth + 0.5;
    if (fits) lo = mid;
    else hi = mid;
  }
  return lo;
}

/** Largest font-size (px) at which single-line `el` fits box `w`×`h`. */
function fitLineSize(el: HTMLElement, w: number, h: number, min: number, max: number): number {
  let lo = min;
  let hi = max;
  for (let i = 0; i < 14; i++) {
    const mid = (lo + hi) / 2;
    el.style.fontSize = `${mid}px`;
    const fits = el.scrollWidth <= w + 0.5 && el.scrollHeight <= h + 0.5;
    if (fits) lo = mid;
    else hi = mid;
  }
  return lo;
}

export function applyFits(root: HTMLElement): void {
  for (const el of root.querySelectorAll<HTMLElement>('[data-fit="fill"]')) {
    // Low floor so a long single word (e.g. "DECORATIONS" in a narrow 6-col
    // board cell) shrinks to fit on one line rather than wrapping mid-word.
    el.style.fontSize = `${fitFillSize(el, 6, 200)}px`;
  }

  // Dollar values: one uniform size across the whole board = the size that fits
  // the widest value in its cell.
  const vals = [...root.querySelectorAll<HTMLElement>('[data-fit="val"]')];
  if (vals.length > 0) {
    let size = 999;
    for (const v of vals) {
      const parent = v.parentElement;
      if (!parent) continue;
      const { w, h } = contentBox(parent);
      size = Math.min(size, fitLineSize(v, w, h, 12, 80));
    }
    for (const v of vals) v.style.fontSize = `${size}px`;
  }
}

/** Install a single debounced resize handler that re-fits the live screen. */
export function installRefit(root: HTMLElement): void {
  let raf = 0;
  window.addEventListener('resize', () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => applyFits(root));
  });
}
